import { Box, Divider, Grid, Tab, Tabs } from "@mui/material";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectListFields, selectSorts } from "../../../../store/selectors/fieldsStateSelectors.ts";
import { selectEditDisabled, selectSelectedBuilderTab } from "../../../../store/selectors/uiSettingsSelectors.ts";
import type { BuilderTab } from "../../../../store/types/uiSettingsState.types.ts";
import { createUiSettingsAction } from "../../../../store/utils/storeUtils.ts";
import { isDimensionBased } from "../../../../utilities/fieldsHelper.ts";
import type { TableComponentProps } from "../../../viewer/common/Types.ts";
import TableDragLayer from "../../common/table/TableDragLayer.tsx";
import { getAvailableSortFields } from "../../common/utilities/sortFieldStateHelper.ts";
import ConditionsDropFieldsContainer from "../../pivot/table/conditions/ConditionsDropFieldsContainer.tsx";
import SortDropsFieldsContainer from "../../tabular/table/SortDropsFieldsContainer.tsx";
import ListFormatContainer from "../format/ListFormatContainer.tsx";
import ListSettingsContainer from "../settings/ListSettingsContainer.tsx";
import ListFieldsDropContainer from "./ListFieldsDropContainer.tsx";

export const ListTableComponent = ({ ReportTypeSelector }: TableComponentProps) => {
  const dispatch = useAppDispatch();
  const selectedTab = useSelector(selectSelectedBuilderTab);
  const sortingAreaValues = useSelector(selectSorts);
  const fieldsAreaValues = useSelector(selectListFields);
  const editDisabled = useSelector(selectEditDisabled);
  const availableSortFields = useMemo(
    () => getAvailableSortFields(fieldsAreaValues.filter(isDimensionBased), sortingAreaValues),
    [fieldsAreaValues, sortingAreaValues]
  );

  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", gap: "1rem", mt: "1rem" }}>
      <Grid sx={{ px: "1.3rem" }}>
        {ReportTypeSelector}
        <Tabs
          value={selectedTab}
          sx={{ borderBottom: "none" }}
          onChange={(_, value: BuilderTab) => dispatch(createUiSettingsAction("setTab", value))}
        >
          <Tab value="Fields" label="Fields" />
          <Tab value="Settings" label="Settings" />
          <Tab value="Format" label="Format" />
        </Tabs>
      </Grid>
      <Divider sx={{ mt: -2 }} />
      {selectedTab === "Fields" && (
        <ScrollableFlexContainer
          scrollContainerSx={{ display: "flex", flexDirection: "column", gap: "1.5rem", px: "1.3rem" }}
        >
          <ConditionsDropFieldsContainer readonlyMode={editDisabled} />
          <ListFieldsDropContainer readonlyMode={editDisabled} />
          <SortDropsFieldsContainer availableFields={availableSortFields} readonlyMode={editDisabled} />
          <TableDragLayer />
        </ScrollableFlexContainer>
      )}
      {selectedTab === "Settings" && <ListSettingsContainer readonlyMode={editDisabled} />}
      {selectedTab === "Format" && <ListFormatContainer readonlyMode={editDisabled} />}
    </Box>
  );
};

export default ListTableComponent;
